import React, { useState, useCallback, useEffect } from "react";

import { Plus } from "icons";

import dataSourcesApi from "apis/dataSource";
import {
  Button,
  PageLoader,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import ContentBox from "components/Common/ContentBox";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import QuickFilter from "components/Common/QuickFilter";
import SummaryBarChart from "components/Common/SummaryBarChart";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import CreateDataSource from "./CreateDataSource";
import DeleteDataSource from "./DeleteDataSource";
import DataSourcesTable from "./Table";
import { DATA_SOURCE_COLUMNS } from "./TableColumn";

import {
  DATA_SOURCES_QUICK_FILTER_DATA,
  DEFAULT_DATA_SOURCES_QUICK_FILTER_DATA,
} from "../constants";

const DataSourcesHome = ({ userPermissions }) => {
  const [isCreateDataSourceOpen, setIsCreateDataSourceOpen] = useState(false);
  const [dataSources, setDataSources] = useState([]);
  const [pagination, setPagination] = useState();
  const [checkedRows, setCheckedRows] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editedDataSourceId, setEditedDataSourceId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [sort, setSort] = useState("top");
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [quickFilterData, setQuickFilterData] = useState(
    DEFAULT_DATA_SOURCES_QUICK_FILTER_DATA
  );
  const [filter, setFilter] = useState({
    filterBy: "",
    filterValue: "",
  });

  const fetchDataSources = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await dataSourcesApi.fetch(
        currentPage,
        pageSize,
        search.toLowerCase(),
        filter.filterBy,
        filter.filterValue
      );
      setDataSources(data);
      setQuickFilterData(DATA_SOURCES_QUICK_FILTER_DATA(setFilter, data));
      setPagination(data.data_sources_pagination);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  const fetchDataSourceSummary = async () => {
    try {
      setIsGraphLoading(true);
      const { data } = await dataSourcesApi.fetchDataSourceSummary(sort);
      setSummaryData(data?.summary_graph);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsGraphLoading(false);
    }
  };

  const handleDataSourceEdit = id => {
    setEditedDataSourceId(id);
    setIsEdit(true);
    setIsCreateDataSourceOpen(true);
  };

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteModalOpen(true);
  };

  const filterParams = useSearchAndPaginate(fetchDataSources, {
    all: () => setCheckedRows([]),
  });

  const columnHeaderSelector = useColumnHeaderSelector(
    DATA_SOURCE_COLUMNS(handleDataSourceEdit, handleDelete, userPermissions)
  );

  useEffect(() => {
    fetchDataSourceSummary();
  }, [sort]);

  return (
    <div className="cs-ui-container cs-ui-container__table">
      {/* Header */}
      <SubHeader
        breadcrumbs={[
          { label: "Data Sources", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Button
              label="Create Data Source"
              icon={Plus}
              onClick={() => {
                setIsCreateDataSourceOpen(true);
                setIsEdit(false);
              }}
            />
          </PermissibleWrapper>
        }
        className="mt-8"
      />
      <ContentBox className="flex flex-col justify-center space-y-4 mt-4 items-center bg-white dark:bg-[#2f3941] w-full dark:text-white">
        <Typography style="h3" type="semibold" className="justify-center flex">
          Summary
        </Typography>
        <div className="flex space-x-4 w-full">
          <QuickFilter className="w-1/2" quickFilterData={quickFilterData} />
          <SummaryBarChart
            topFiveAction={() => setSort("top")}
            bottomFiveAction={() => setSort("bottom")}
            className="w-1/2"
            data={summaryData}
            isLoading={isGraphLoading}
            title="Percentage mapped DS to CDE"
            isValuePercent
          />
        </div>
      </ContentBox>
      <FilterHeader
        entity="Data Source"
        filterParams={filterParams}
        columnHeaderSelector={columnHeaderSelector}
        count={pagination?.total_pagination}
        checkedRows={checkedRows}
        setIsDeleteAlertOpen={setIsDeleteModalOpen}
        userPermissions={userPermissions}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <DataSourcesTable
          dataSources={dataSources.data_sources}
          setIsCreateDataSourceOpen={setIsCreateDataSourceOpen}
          setIsEdit={setIsEdit}
          userPermissions={userPermissions}
          columns={columnHeaderSelector.columns}
          setCheckedRows={setCheckedRows}
          checkedRows={checkedRows}
          pagination={pagination}
          filterParams={filterParams}
        />
      )}
      {/*Header end */}
      <CreateDataSource
        isCreateDataSourceOpen={isCreateDataSourceOpen}
        setIsCreateDataSourceOpen={setIsCreateDataSourceOpen}
        fetchDataSources={fetchDataSources}
        isEdit={isEdit}
        currentDataSourceId={editedDataSourceId}
      />
      <DeleteDataSource
        fetchDataSources={fetchDataSources}
        setIsLoading={setIsLoading}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
      />
    </div>
  );
};

export default DataSourcesHome;
